import { isEmpty, map, pipe, reject, slice } from 'ramda';
import React, { FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { Tooltip } from '@wix/design-system';
import { Text } from '../../../../../components/common';
import { useMMGRSettings } from '../../../../../utils/hoc/withMMGRSettings';
import {
  TrackMediaManagerTeammatesViewClickOrigin,
  useTrackTeamAnalytics,
} from '../../../../../utils/hooks/bi/useTrackTeamAnalytics';
import { MediaManagerSDKTeamMember } from '../../../../../utils/media-manager-sdk';
import { Separator } from '../Separator';
import styles from './Team.scss';
import { IOuter } from './Team.types';
import { Teammate } from './Teammate/Teammate';
import { TeammatesMenu } from './TeammatesMenu/TeammatesMenu';
import { findOwner, isOwner } from './utils';

const MAX_DISPLAYABLE_TEAMMATES_COUNT = 5;

export const Team: FC<IOuter> = ({ board }) => {
  const {
    mmgrSettings: { team },
  } = useMMGRSettings();
  const { trackMediaManagerTeammatesViewClick } = useTrackTeamAnalytics();

  const handleMoreCountClick = useCallback(() => {
    trackMediaManagerTeammatesViewClick(board, {
      clickOrigin: TrackMediaManagerTeammatesViewClickOrigin.CreateNewBoard,
    });
  }, [trackMediaManagerTeammatesViewClick]);

  const owner = findOwner(team);
  const hasTooManyTeammates = team.members.length - 1 > MAX_DISPLAYABLE_TEAMMATES_COUNT;
  const notDisplayedTeammatesCount = team.members.length - MAX_DISPLAYABLE_TEAMMATES_COUNT;

  const teammates = pipe<MediaManagerSDKTeamMember[], MediaManagerSDKTeamMember[], MediaManagerSDKTeamMember[]>(
    reject(isOwner),
    slice(0, MAX_DISPLAYABLE_TEAMMATES_COUNT - 1),
  )(team.members);

  const hasTeammates = !isEmpty(teammates);

  return (
    <div data-hook="team">
      <Text weight="bold" className={styles.title} tagName="div">
        <FormattedMessage id="boards.teams.your-teammates" />
      </Text>
      <div className={styles.teammates}>
        {owner && <Teammate className={styles.owner} teamMember={owner} />}
        {hasTeammates && <div className={styles.verticalSeparator} />}
        {map(
          (teamMember) => (
            <Teammate className={styles.teammate} teamMember={teamMember} key={teamMember.userId} />
          ),
          teammates,
        )}
        {hasTooManyTeammates && (
          <TeammatesMenu board={board} team={team} showManageButton>
            <div className={styles.moreCount} onClick={handleMoreCountClick} data-hook="more-count">
              <Tooltip
                content={
                  <FormattedMessage id="boards.teams.more-count" values={{ count: notDisplayedTeammatesCount }} />
                }
                appendTo="window"
                moveBy={{ y: 15 }}
              >
                <Text weight="bold" size="tiny">
                  <FormattedMessage id="boards.teams.count" values={{ count: notDisplayedTeammatesCount }} />
                </Text>
              </Tooltip>
            </div>
          </TeammatesMenu>
        )}
      </div>
      <Separator />
    </div>
  );
};
